import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuthConfig, useMe } from '../api/hooks';
import { BrandLockup } from '../components/BrandLogo';
import { SiteFooter } from '../components/SiteFooter';
import { de } from '../i18n/de';
import { APP_HOME } from '../lib/nextPath';

const L = de.landing;

/** Vorschau der App: Zeitlinie mit Szenen, Erzähllinie mit Kapiteln, eine Karte der Bibliothek und eine Manuskriptseite (nur Gestaltung). */
function HeroVisual() {
  const v = L.visual;
  const [arrival, storm, escape, farewell] = v.scenes;
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hv-window">
        <div className="hv-top">
          <span className="hv-dots">
            <i />
            <i />
            <i />
          </span>
          <span className="hv-tabs">
            {v.tabs.map((t, i) => (
              <span key={t} className={i === 1 ? 'on' : undefined}>
                {t}
              </span>
            ))}
          </span>
        </div>
        <div className="hv-stage">
          <div className="hv-timeline">
            <span className="hv-period lane2" style={{ left: '47%', width: '20%' }}>
              <b>{escape}</b>
            </span>
            <span className="hv-period" style={{ left: '31%', width: '33%' }}>
              <b>{storm}</b>
            </span>
            <span className="hv-axis" />
            <span className="hv-tick" style={{ left: '4%' }} />
            <span className="hv-tick" style={{ left: '96%' }} />
            <span className="hv-flag" style={{ left: '4%' }}>
              {v.start}
            </span>
            <span className="hv-flag" style={{ left: '96%' }}>
              {v.end}
            </span>
            <span className="hv-event" style={{ left: '16%' }}>
              <b>{arrival}</b>
            </span>
            <span className="hv-event" style={{ left: '82%' }}>
              <b>{farewell}</b>
            </span>
          </div>
          <div className="hv-band">
            {v.chapters.map((c, i) => (
              <div key={c} className="hv-chapter">
                <span className="hv-chapter-title">{c}</span>
                <span className="hv-tiles">
                  {Array.from({ length: [2, 3, 1][i]! }, (_, k) => (
                    <i key={k} className={`t${(i + k) % 4}`} />
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="hv-card">
        <span className="hv-avatar">{v.person.slice(0, 1)}</span>
        <span>
          <b>{v.person}</b>
          <small>{v.role}</small>
        </span>
      </div>
      <div className="hv-page">
        <span className="hv-page-title">{v.chapter}</span>
        <p>
          {v.text[0]}
          <mark>{v.text[1]}</mark>
          {v.text[2]}
        </p>
        <i className="hv-line" />
        <i className="hv-line" />
        <i className="hv-line short" />
      </div>
    </div>
  );
}

/**
 * Öffentliche Startseite: worum es geht (mit einer Vorschau der App) und die Beta. Abgemeldet führt sie zu Registrierung und
 * Anmeldung (je nach Einstellung des Servers offen, mit Einladungscode oder geschlossen), angemeldet zu den Geschichten.
 */
export function LandingPage() {
  const { data: me } = useMe();
  const { data: cfg } = useAuthConfig();
  const loggedIn = !!me;
  const registration = cfg?.registration ?? 'open';

  const cta = (extra?: ReactNode): ReactNode =>
    loggedIn ? (
      <Link className="btn primary big" to={APP_HOME} data-testid="landing-to-app">
        {L.toApp}
      </Link>
    ) : registration === 'closed' ? (
      <>
        <div className="notice landing-closed">{L.closed}</div>
        <Link className="btn big" to="/login">
          {L.login}
        </Link>
      </>
    ) : (
      <>
        <Link className="btn primary big" to="/register" data-testid="landing-register">
          {registration === 'invite' ? L.ctaInvite : L.ctaOpen}
        </Link>
        {extra}
      </>
    );

  return (
    <div className="landing" data-testid="landing">
      <div className="landing-wrap">
        <header className="landing-head">
          <Link to="/" className="landing-brand" aria-label={de.app.name}>
            <BrandLockup />
            <span className="beta-pill">{L.beta}</span>
          </Link>
          <nav className="landing-nav" aria-label={L.nav}>
            {loggedIn ? (
              <Link className="btn primary" to={APP_HOME}>
                {L.toApp}
              </Link>
            ) : (
              <>
                <Link className="btn ghost" to="/login" data-testid="landing-login">
                  {L.login}
                </Link>
                {registration !== 'closed' && (
                  <Link className="btn primary landing-nav-register" to="/register">
                    {L.register}
                  </Link>
                )}
              </>
            )}
          </nav>
        </header>

        <main>
          <section className="landing-hero">
            <div className="landing-hero-text">
              <div className="landing-eyebrow">{L.eyebrow}</div>
              <h1>{L.title}</h1>
              <p className="landing-lead">{L.lead}</p>
              <div className="landing-cta">
                {cta(
                  <Link className="btn big" to="/login">
                    {L.haveAccount}
                  </Link>,
                )}
              </div>
              {!loggedIn && registration === 'invite' && <p className="muted small landing-note">{L.inviteNote}</p>}
            </div>
            <HeroVisual />
          </section>

          <section className="landing-term" aria-label={L.term.label}>
            <dl>
              <dt>
                <span className="landing-term-word">{L.term.word}</span> · <span className="landing-term-origin">{L.term.origin}</span>
              </dt>
              {L.term.meanings.map((m) => (
                <dd key={m}>{m}</dd>
              ))}
            </dl>
          </section>

          <section className="landing-section landing-beta" aria-labelledby="landing-beta">
            <div className="landing-beta-card">
              <span className="beta-pill">{L.beta}</span>
              <h2 id="landing-beta">{L.betaTitle}</h2>
              <p>{L.betaText}</p>
              <div className="landing-cta">{cta()}</div>
            </div>
          </section>
        </main>

        <SiteFooter>
          {!loggedIn && (
            <>
              <Link to="/login">{L.login}</Link>
              {registration !== 'closed' && <Link to="/register">{L.register}</Link>}
            </>
          )}
        </SiteFooter>
      </div>
    </div>
  );
}
